/**
 * Equipo — quién entra al taller y con qué llaves (equipo.html): las 4
 * personas del Skein Team son SEMILLA fiel del canon (personas.seed.ts;
 * el directorio real llega con F5b). La fila de quien ha iniciado sesión
 * se identifica por email real contra /studio/me (useMe) y muestra su
 * nombre/email reales con «tú» — el resto queda tal cual el canon.
 * Visibilidad ≠ autorización EN PANTALLA: los mandos de admin se ven
 * pero salen deshabilitados con su porqué en tooltip, tal como los fija
 * el prototipo — no hay un segundo estado «viewer admin» diseñado, así
 * que no se inventa uno (el prototipo es el contrato visual). «Vacío»
 * n/a: la plantilla siempre deja al menos un admin.
 */
import { useEffect } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { Badge, Banner, Button, buttonClass, CellPerson, Fainted, Mono, PageHead, RowActions, Skeleton, Table, TdNum, Th } from "@skein/ui";
import { useMe } from "../../api/queries";
import { initials } from "../../auth/initials";
import { PERSONAS } from "./personas.seed";
import { personaVista } from "./vista";

function SkeletonRows() {
  return (
    <tbody>
      {PERSONAS.map((_, i) => (
        <tr key={i}>
          <td><Skeleton style={{ width: "14rem", maxWidth: "100%" }} /></td>
          <td><Skeleton style={{ width: "4rem" }} /></td>
          <td><Skeleton style={{ width: "5rem" }} /></td>
          <TdNum><Skeleton style={{ width: "1.5rem", marginLeft: "auto" }} /></TdNum>
          <td />
        </tr>
      ))}
    </tbody>
  );
}

export function EquipoPage() {
  const me = useMe();
  const search = useSearch({ strict: false }) as { estado?: string };

  // useMe no lleva retryEvery30s (compartido con el shell): reintento local, solo aquí.
  useEffect(() => {
    if (!me.isError) return;
    const id = setInterval(() => void me.refetch(), 30_000);
    return () => clearInterval(id);
  }, [me.isError, me.refetch]);

  const forced = search.estado;
  const view = forced === "cargando" || forced === "error" ? forced : me.isPending ? "cargando" : me.isError ? "error" : "lista";

  const principal = me.data?.principal;
  const nAdmins = PERSONAS.filter((p) => p.rol === "admin").length;
  const nCuracion = PERSONAS.length - nAdmins;

  const tabla = (
    <Table stale={view === "error"} busy={view === "cargando"} label={view === "cargando" ? "Cargando el equipo" : "Equipo"} style={{ maxWidth: "64rem" }}>
      <thead><tr><Th>Persona</Th><Th>Rol</Th><Th>Último acceso</Th><Th num>Actos hoy</Th><Th aria-label="Acciones" /></tr></thead>
      {view === "cargando" ? <SkeletonRows /> : (
        <tbody>
          {PERSONAS.map((p) => {
            const { nombre, email, esTu } = personaVista(p, principal?.email, principal?.name);
            return (
              <tr key={p.email}>
                <CellPerson avatar={initials(nombre)} name={nombre} sub={esTu ? `${email} · eres tú` : email} />
                <td>{p.rol === "admin" ? <Badge tone="accent">admin</Badge> : <Badge>{p.rol}</Badge>}</td>
                <td><Mono><Fainted>{p.ultimoAcceso}</Fainted></Mono></td>
                <TdNum>{p.actosHoy ? p.actosHoy : "—"}</TdNum>
                {p.rol === "admin" ? (
                  <RowActions><Button variant="quiet" size="s" disabled tip="Solo un admin gestiona roles" tipPos="left">Cambiar rol</Button></RowActions>
                ) : (
                  <RowActions><Link to="/auditoria" className={buttonClass({ size: "s", variant: "quiet" })}>Sus actos</Link></RowActions>
                )}
              </tr>
            );
          })}
        </tbody>
      )}
    </Table>
  );

  return (
    <>
      <PageHead
        title="Equipo"
        meta={view === "cargando" ? <Skeleton style={{ width: "18rem", height: "1.25em" }} /> : <>
          <Badge>{PERSONAS.length} personas</Badge>
          <Badge tone="accent">{nAdmins} admins</Badge>
          <Badge>{nCuracion} en curación</Badge>
          <Fainted style={{ fontSize: "var(--sk-fs-2xs)", alignSelf: "center" }}>ver no es poder: los mandos de admin se ganan, no se muestran</Fainted>
        </>}
        actions={<Button variant="primary" disabled tip="Solo un admin invita al equipo" tipPos="left">Invitar…</Button>}
      />
      <div className="listzone">
        {view === "error" && (
          <Banner tone="danger">
            <b>No llego al equipo.</b> Lo que ves puede estar desactualizado. Reintento solo cada 30 s. <Button variant="link" onClick={() => void me.refetch()}>reintentar ahora</Button>
          </Banner>
        )}
        {tabla}
        {view !== "cargando" && (
          <Fainted block style={{ marginTop: "var(--sk-sp-4)", fontSize: "var(--sk-fs-xs)", maxWidth: "64rem" }}>
            Los «actos hoy» salen de la <Link to="/auditoria" className={buttonClass({ variant: "link" })}>auditoría</Link>: 3 de Núria (un insight aplicado, un draft creado, un descarte) y 1 de Eloi (la activación de Talleres Comas). Invitar, cambiar roles y retirar accesos exige rol de admin — Núria lo ve, pero no lo puede pulsar.
          </Fainted>
        )}
      </div>
    </>
  );
}
